'use client'
import type { Form as FormType } from '@payloadcms/plugin-form-builder/types'

import { useRouter } from 'next/navigation'
import React, { useCallback, useEffect, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import RichText from '@/ui/components/RichText'
import { Button } from '@/ui/atoms/Button'
import type { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'

import { buildInitialFormState } from './buildInitialFormState'
import { fields } from './fields'
import { getClientSideURL } from '@/utils/getURL'
import { Spinner } from '@/ui/atoms/Spinner'
import { LoggedInUser } from './User'
import { SentFromPath } from './Path'
import { Honeypot } from './Honeypot'
import { cn } from '@/utils/ui'
import { LoggedInUserMail } from './User/mail'
import { trackEvent } from '@/lib/analytics/posthog'
export type Value = unknown

export interface Property {
  [key: string]: Value
}

export interface Data {
  [key: string]: Property | Property[]
}

export type FormBlockType = {
  blockName?: string
  blockType?: 'formBlock'
  enableIntro: boolean
  form: FormType & { honeyPotNameField?: string }
  introContent?: SerializedEditorState
  removeAfterSubmission: boolean
  title?: string
  description?: string
  hasLightBackground?: boolean
  isModal?: boolean
}

export const FormBlockComponent: React.FC<
  {
    id?: string
    formKey?: React.Key
  } & FormBlockType
> = (props) => {
  const {
    enableIntro,
    removeAfterSubmission,
    form: formFromProps,
    form: {
      id: formID,
      confirmationMessage,
      confirmationType,
      redirect,
      submitButtonLabel,
      honeyPotNameField = 'honeyPot',
    } = {},
    introContent,
    title,
    description,
    hasLightBackground = true,
    isModal = false,
    formKey,
  } = props

  const formIDWithKey = formID + '-' + formKey

  const formMethods = useForm<any>({
    defaultValues: {
      ...buildInitialFormState(formFromProps.fields),
      UserMail: '',
      loggedInUser: '',
      sentFromPath: '',
    },
  })
  const {
    control,
    formState: { errors },
    formState: { isSubmitSuccessful },
    formState,
    handleSubmit,
    register,
    reset,
  } = formMethods

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>()
  const [error, setError] = useState<{ message: string; status?: string } | undefined>()
  const [honeyPotName, setHoneyPotName] = useState<string>('')
  const router = useRouter()

  const onSubmit = useCallback(
    (data: Data) => {
      let loadingTimerID: ReturnType<typeof setTimeout>
      const submitForm = async () => {
        setError(undefined)

        const dataToSend = Object.entries(data).map(([name, value]) => ({
          field: name,
          value,
        }))

        let dataToSendFiltered = dataToSend

        if (honeyPotName !== '') {
          const honeypotField = dataToSend.filter((item) => item.field === honeyPotName)
          if (honeypotField?.at(0)?.value.toString() !== '') {
            return
          } else {
            dataToSendFiltered = dataToSend.filter((item) => item.field !== honeyPotName)
          }
        }

        loadingTimerID = setTimeout(() => {
          setIsLoading(true)
        }, 1000)

        try {
          // MIGRATION (5.3): static builds capture via the Payload-free endpoint; flag off =
          // the original Payload REST endpoint, byte-identical.
          const endpoint =
            process.env.NEXT_PUBLIC_STATIC_FORMS === '1'
              ? `${getClientSideURL()}/api/forms/submit`
              : `${getClientSideURL()}/api/form-submissions`
          const req = await fetch(endpoint, {
            body: JSON.stringify({
              form: formID,
              submissionData: dataToSendFiltered,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'POST',
          })

          const res = await req.json()

          clearTimeout(loadingTimerID)

          if (req.status >= 400) {
            setIsLoading(false)
            setError({
              message: res.errors?.[0]?.message || 'Internal Server Error',
              status: res.status,
            })
            return
          }

          setIsLoading(false)
          setHasSubmitted(true)
          trackEvent('question_submitted', {
            form_id: formID,
            form_title: formFromProps?.title || title,
            page_path: window.location.pathname,
          })

          if (confirmationType === 'redirect' && redirect) {
            const { url } = redirect
            if (url) router.push(url)
          }
        } catch (err) {
          console.warn(err)
          setIsLoading(false)
          setError({
            message: 'Something went wrong.',
          })
        }
      }

      void submitForm()
    },
    [router, formID, redirect, confirmationType, honeyPotName],
  )

  useEffect(() => {
    if (isSubmitSuccessful) {
      formMethods.reset()
    }
  }, [formState, hasSubmitted, reset])

  return (
    <div
      className={cn(
        ` w-full  rounded-md ${isModal ? 'lg:w-[428px] !bg-bg-subtle' : 'lg:w-[666px] min-h-[483px] lg:min-h-[458px] lg:p-card-md p-mobile-card-md'}`,
        hasLightBackground ? 'bg-white' : 'bg-bg-base-transparent-95 backdrop-blur-lg',
      )}
      key={formKey}
    >
      <div className="flex flex-col gap-8 ">
        {/* Form Title Section */}
        {!hasLightBackground && (
          <div className={`flex flex-col  ${isModal ? 'gap-6' : 'gap-4'}`}>
            <h2 className="text-headline-S-semibold text-text-base">
              {title || formFromProps?.title || 'Contact Form'}
            </h2>
            {(description || introContent) && (
              <p className="text-body-S-regular text-text-subtle">
                {description || (introContent && <RichText data={introContent} />)}
              </p>
            )}
          </div>
        )}

        {/* Form Content */}
        <FormProvider {...formMethods}>
          {(!hasSubmitted || (hasSubmitted && !removeAfterSubmission)) && (
            <form
              id={formIDWithKey}
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
              noValidate
            >
              <div className="flex flex-col gap-4">
                <LoggedInUserMail register={register} />
                {formFromProps &&
                  formFromProps.fields &&
                  formFromProps.fields?.map((field, index) => {
                    const Field: React.FC<any> = fields?.[field.blockType as keyof typeof fields]
                    if (Field) {
                      return (
                        <div key={index}>
                          <Field
                            form={formFromProps}
                            {...field}
                            {...formMethods}
                            control={control}
                            errors={errors}
                            register={register}
                            setHoneyPotName={setHoneyPotName}
                            showLabelAndDescription={hasLightBackground}
                          />
                        </div>
                      )
                    }
                    return null
                  })}
              </div>
              <Honeypot
                name={honeyPotNameField}
                register={register}
                setHoneyPotName={setHoneyPotName}
              />
              <LoggedInUser register={register} />
              <SentFromPath register={register} />

              <div className="flex justify-end">
                <Button
                  form={formIDWithKey}
                  type="submit"
                  variant="primary"
                  disabled={isLoading}
                  className="flex items-center justify-center gap-2 h-[38px] rounded"
                >
                  <span>{submitButtonLabel || 'Submit'}</span>
                  {isLoading && <Spinner />}
                </Button>
              </div>
            </form>
          )}

          {!isLoading && hasSubmitted && confirmationType === 'message' && (
            <div className="mt-4">
              <RichText data={confirmationMessage} />
            </div>
          )}

          {error && (
            <React.Fragment>
              <div className="mt-4 text-red-500">{`${error.status || '500'}: ${error.message || ''}`}</div>
              <div className="mt-4">{JSON.stringify(error)}</div>
            </React.Fragment>
          )}
        </FormProvider>
      </div>
    </div>
  )
}
